/**
 * As chamadas do acompanhamento da inscrição (F06). Todas com a sessão do
 * inscrito (F04): `publica` (sem token do Keycloak) e `credenciais` (manda o
 * cookie `HttpOnly`). A API deriva o dono da sessão; o protocolo do caminho só
 * escolhe qual das inscrições DA CONTA ler.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';

import {
  type DetalheDoVeiculo,
  type EntidadeInscrita,
  type OpcoesDeCorrecao,
  type RespostaDeCorrecao,
} from './AcompanhamentoContrato';

const BASE = '/bff/v1/acompanhamento-inscricao';
const COM_SESSAO = { publica: true, credenciais: true } as const;

export const CHAVES_DO_ACOMPANHAMENTO = {
  todas: ['portal', 'acompanhamento'] as const,
  minhas: ['portal', 'acompanhamento', 'minhas'] as const,
  veiculo: (protocolo: string) => ['portal', 'acompanhamento', 'veiculo', protocolo] as const,
  opcoes: (protocolo: string) => ['portal', 'acompanhamento', 'opcoes', protocolo] as const,
};

/**
 * Recusa (4xx) não se repete sozinha: 401 pede entrar, 404 é definitivo. Falha
 * técnica tenta mais uma vez antes de mostrar o erro.
 */
function repetirSoFalhaTecnica(tentativas: number, erro: ErroApi): boolean {
  return tentativas < 1 && !(erro instanceof ErroApi && erro.situacao < 500);
}

export function useMinhasInscricoes(
  habilitado: boolean,
): UseQueryResult<{ entidades: EntidadeInscrita[] }, ErroApi> {
  return useQuery<{ entidades: EntidadeInscrita[] }, ErroApi>({
    queryKey: CHAVES_DO_ACOMPANHAMENTO.minhas,
    queryFn: ({ signal }) => chamarApi(`${BASE}/minhas`, { ...COM_SESSAO, sinal: signal }),
    enabled: habilitado,
    retry: repetirSoFalhaTecnica,
  });
}

export function useVeiculoInscrito(
  protocolo: string | undefined,
  habilitado: boolean,
): UseQueryResult<DetalheDoVeiculo, ErroApi> {
  return useQuery<DetalheDoVeiculo, ErroApi>({
    queryKey: CHAVES_DO_ACOMPANHAMENTO.veiculo(protocolo ?? ''),
    queryFn: ({ signal }) =>
      chamarApi(`${BASE}/veiculos/${encodeURIComponent(protocolo ?? '')}`, {
        ...COM_SESSAO,
        sinal: signal,
      }),
    enabled: habilitado && Boolean(protocolo),
    retry: repetirSoFalhaTecnica,
  });
}

export function useOpcoesDeCorrecao(
  protocolo: string,
  habilitado: boolean,
): UseQueryResult<OpcoesDeCorrecao, ErroApi> {
  return useQuery<OpcoesDeCorrecao, ErroApi>({
    queryKey: CHAVES_DO_ACOMPANHAMENTO.opcoes(protocolo),
    queryFn: ({ signal }) =>
      chamarApi(`${BASE}/veiculos/${encodeURIComponent(protocolo)}/opcoes-de-correcao`, {
        ...COM_SESSAO,
        sinal: signal,
      }),
    enabled: habilitado,
    staleTime: 5 * 60_000,
    retry: repetirSoFalhaTecnica,
  });
}

/**
 * Responde a devolução para correção. O detalhe que volta substitui o do
 * cache, e a lista é recarregada — o status do veículo mudou.
 */
export function useResponderCorrecao(protocolo: string) {
  const cliente = useQueryClient();
  return useMutation<DetalheDoVeiculo, ErroApi, RespostaDeCorrecao>({
    mutationFn: (resposta) =>
      chamarApi(`${BASE}/veiculos/${encodeURIComponent(protocolo)}/correcao`, {
        ...COM_SESSAO,
        metodo: 'POST',
        corpo: resposta,
      }),
    onSuccess: (detalhe) => {
      cliente.setQueryData(CHAVES_DO_ACOMPANHAMENTO.veiculo(protocolo), detalhe);
      void cliente.invalidateQueries({ queryKey: CHAVES_DO_ACOMPANHAMENTO.minhas });
    },
  });
}
